import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, Building2, ArrowRight,
  Shield, BarChart2, Globe, ChevronLeft, ChevronRight,
  Network,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/* ─── Slide data ─────────────────────────────────────────────── */
const slides = [
  {
    icon: Building2,
    tag: 'Strategic Growth',
    headline: ['Capital Deployed', 'with Purpose and', 'Precision'],
    body: 'Our structured financial products and advisory services are built for institutional resilience combining global perspective with deep local intelligence.',
    cta: 'View Investment Sectors',
    ctaLink: '/investment-sectors',
    image: 'about_us1.jpeg',
    accent: '#a78bfa',
    accentRgb: '167,139,250',
    accentLight: '#f5f3ff',
    mainImage: '/z_17.png',
    statValue: 'Structured Solutions',
    statLabel: 'Capital Framework',
  },
  {
    icon: TrendingUp,
    tag: 'Multi-Sector Verticals',
    headline: ['Real Estate.', 'Equities. Bullion.', 'Ventures.'],
    body: "A diversified investment portfolio built for long-term capital appreciation and income generation, spanning four distinct verticals, each chosen for its compounding potential and alignment with India's growth story.",
    cta: 'Explore Opportunities',
    ctaLink: '/investment-sectors',
    image: '/capital_market2.jpg',
    accent: '#f59e0b',
    accentRgb: '245,158,11',
    accentLight: '#fef9c3',
    mainImage: '/hero_sec1.png',
    statValue: '4 Verticals',
    statLabel: 'Diversified Portfolio',
  },
  {
    icon: Network,
    tag: 'Our Subsidiaries',
    headline: ['Subsidiaries', 'Built for', 'Diversification'],
    body: 'Four focused subsidiaries spanning cinema, commodity trading, global logistics, and real estate, each independently operated, collectively strengthening the Fundviser Capital across diverse sectors.',
    cta: 'Our Business',
    ctaLink: '/what-we-do',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=1080&fit=crop&q=80',
    accent: '#34d399',
    accentRgb: '52,211,153',
    accentLight: '#d1fae5',
    mainImage: '/subs_3.png',
    statValue: '4',
    statLabel: 'Active Subsidiaries',
    logos: [
      { src: '/starlight-logo-new.png', name: 'Starlight Box Theatres' },
      { src: '/silversage-bgremoved.png',   name: 'Silver Sage Trading' },
      { src: '/dars_logo.png',          name: 'DARS Transtrade' },
      { src: '/newindialogo.png',       name: 'New India RE & Infra' },
    ],
  },
];

/* ─── Metrics ────────────────────────────────────────────────── */
const metrics = [
  { icon: BarChart2, value: 'BSE Listed',  label: 'Code 530197' },
  { icon: Shield,    value: '30+ Years',   label: 'Market Experience' },
  { icon: Globe,     value: 'Diversified', label: 'Portfolio' },
];

/* ─── Card shell ─────────────────────────────────────────────── */
const card3D = {
  background: '#ffffff',
  borderRadius: '16px',
  border: '1px solid rgba(218,224,235,0.85)',
  boxShadow:
    '-8px 8px 0px rgba(180,190,210,0.35), ' +
    '0 12px 28px rgba(0,0,0,0.10), ' +
    '0 3px 6px rgba(0,0,0,0.05)',
};

/* ─── Component ──────────────────────────────────────────────── */
const HeroSection = () => {
  const [current, setCurrent]     = useState(0);
  const [, setPrev]               = useState(null);
  const [animating, setAnimating] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [textIn,   setTextIn]     = useState(true);

  const intervalRef = useRef(null);
  const progressRef = useRef(null);
  const DURATION    = 6000;

  const startTimer = () => {
    clearInterval(intervalRef.current);
    clearInterval(progressRef.current);
    setProgress(0);
    const step = 100 / (DURATION / 50);
    progressRef.current = setInterval(() => setProgress(p => Math.min(p + step, 100)), 50);
    intervalRef.current = setInterval(() => navigate('next'), DURATION);
  };

  const navigate = (dir, target) => {
    if (animating) return;
    setAnimating(true);
    setTextIn(false);
    setTimeout(() => {
      setPrev(current);
      setCurrent(prev => {
        if (target !== undefined) return target;
        return dir === 'next'
          ? (prev + 1) % slides.length
          : (prev - 1 + slides.length) % slides.length;
      });
      setAnimating(false);
      setTimeout(() => setTextIn(true), 60);
    }, 450);
    startTimer();
  };

  const goToIndex = i => { if (i !== current) navigate(null, i); };

  useEffect(() => {
    setTextIn(true);
    startTimer();
    return () => { clearInterval(intervalRef.current); clearInterval(progressRef.current); };
  }, []); // eslint-disable-line

  const slide = slides[current];
  const ease  = (delay) => `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`;

  return (
    <div
      className="relative md:min-h-screen flex flex-col overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #0f0225 0%, #1c0840 55%, #110230 100%)' }}
    >
      {/* ── Background ambience ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div style={{
          position: 'absolute', top: '-12%', right: '-4%', width: 820, height: 820,
          background: 'radial-gradient(circle, rgba(90,2,140,0.45) 0%, rgba(54,1,83,0.18) 45%, transparent 70%)',
        }} />
        <div style={{
          position: 'absolute', bottom: '-18%', left: '-10%', width: 640, height: 640,
          background: 'radial-gradient(circle, rgba(205,150,35,0.14) 0%, transparent 65%)',
        }} />
        <div style={{
          position: 'absolute', top: '20%', right: '22%', width: 480, height: 480,
          background: 'radial-gradient(circle, rgba(54,1,83,0.22) 0%, transparent 65%)',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: '38px 38px',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 80% 80% at 50% 50%, transparent 50%, rgba(8,1,20,0.55) 100%)',
        }} />
      </div>

      {/* ── Mobile progress line ── */}
      <div className="absolute top-0 left-0 right-0 z-30 flex md:hidden h-[3px] bg-white/10">
        {slides.map((s, i) => (
          <div key={i} className="flex-1 relative overflow-hidden"
            style={{ background: i < current ? s.accent : 'transparent' }}>
            {i === current && (
              <div className="absolute inset-0"
                style={{ width: `${progress}%`, background: s.accent, transition: 'width 0.05s linear' }} />
            )}
          </div>
        ))}
      </div>

      {/* ── Main layout ── */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-16 w-full pt-20 pb-10 md:pt-24 md:pb-16 lg:py-24">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-10 xl:gap-16 items-center">

            {/* ══ LEFT — Content ══════════════════════════════════ */}
            <div className="flex flex-col">

              {/* Tag pill */}
              <div
                className="inline-flex items-center gap-2 self-start mb-5 px-4 py-1.5 rounded-full text-xs font-bold tracking-[0.16em] uppercase"
                style={{
                  background: 'rgba(205,150,35,0.12)',
                  border: '1px solid rgba(205,150,35,0.30)',
                  color: '#e8b84b',
                  opacity: textIn ? 1 : 0,
                  transform: textIn ? 'translateY(0)' : 'translateY(-14px)',
                  transition: ease(0),
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#e8b84b', display: 'inline-block' }} />
                {slide.tag}
              </div>

              {/* Headline */}
              <div className="mb-5 space-y-0.5">
                {slide.headline.map((line, li) => (
                  <h1
                    key={`${current}-${li}`}
                    className="font-black leading-[1.05] tracking-tight"
                    style={{
                      fontSize: 'clamp(1.95rem, 4.2vw, 3.7rem)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #e8d8ff 40%, #e8b84b 75%, #f0c860 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      opacity: textIn ? 1 : 0,
                      transform: textIn ? 'translateY(0)' : 'translateY(28px)',
                      transition: ease(70 + li * 85),
                    }}
                  >
                    {line}
                  </h1>
                ))}
              </div>

              {/* Body */}
              <p
                className="text-sm sm:text-base leading-relaxed mb-7 max-w-lg"
                style={{
                  color: 'rgba(220,210,240,0.72)',
                  opacity: textIn ? 1 : 0,
                  transform: textIn ? 'translateY(0)' : 'translateY(18px)',
                  transition: ease(340),
                }}
              >
                {slide.body}
              </p>

              {/* ── Mobile image card (phones only) ── */}
              <div
                className="block md:hidden mb-5 rounded-2xl overflow-hidden"
                style={{ ...card3D, opacity: textIn ? 1 : 0, transition: ease(400) }}
              >
                {slide.logos ? (
                  <div style={{ display: 'flex', flexDirection: 'column', background: '#fff' }}>
                    {slide.logos.map((logo, li) => (
                      <div key={li} style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '14px 28px',
                        borderBottom: li < slide.logos.length - 1 ? '1px solid #f1f5f9' : 'none',
                      }}>
                        <img src={logo.src} alt={logo.name} style={{ maxHeight: li === 1 ? 60 : 44, maxWidth: '75%', objectFit: 'contain' }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                    {slides.map((s, i) => (
                      <img key={i} src={s.mainImage} alt={s.tag}
                        style={{
                          position: 'absolute', inset: 0,
                          width: '100%', height: '100%', objectFit: 'cover',
                          objectPosition: i === 1 ? '50% 65%' : 'center',
                          opacity: current === i ? 1 : 0,
                          transform: current === i ? 'scale(1.06)' : 'scale(1)',
                          transition: 'opacity 1s ease, transform 9s ease',
                        }}
                      />
                    ))}
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 55%)' }} />
                    <span style={{
                      position: 'absolute', top: 12, left: 12,
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: 'rgba(255,255,255,0.90)', backdropFilter: 'blur(8px)',
                      color: slide.accent, padding: '4px 12px', borderRadius: 20,
                      fontSize: 10, fontWeight: 700, transition: 'color 0.8s',
                    }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: slide.accent, display: 'inline-block', transition: 'background 0.8s' }} />
                      {slide.tag}
                    </span>
                  </div>
                )}
              </div>

              {/* CTAs — tablet and up */}
              <div
                className="hidden md:flex flex-wrap gap-3 mb-5"
                style={{
                  opacity: textIn ? 1 : 0,
                  transform: textIn ? 'translateY(0)' : 'translateY(14px)',
                  transition: ease(430),
                }}
              >
                <Link
                  to={slide.ctaLink}
                  className="group inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl font-bold text-[15px] hover:scale-105 hover:-translate-y-0.5 transition-all duration-200"
                  style={{
                    background: 'linear-gradient(135deg, #cd9623, #e8b84b)',
                    color: '#1a0030',
                    boxShadow: '0 8px 28px rgba(205,150,35,0.40)',
                  }}
                >
                  {slide.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/the-firm"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-[15px] hover:scale-105 transition-all duration-200"
                  style={{
                    color: 'rgba(255,255,255,0.88)',
                    border: '1px solid rgba(255,255,255,0.28)',
                    background: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  About the Firm
                </Link>
              </div>

              {/* Slide indicator */}
              <div
                className="flex items-center gap-3 mb-8"
                style={{
                  opacity: textIn ? 1 : 0,
                  transition: ease(500),
                }}
              >
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToIndex(i)}
                    style={{
                      height: 4, width: current === i ? 22 : 4, borderRadius: 4,
                      background: current === i ? '#e8b84b' : 'rgba(255,255,255,0.25)',
                      border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0,
                      transition: 'width 0.4s ease, background 0.8s ease',
                    }}
                  />
                ))}
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', fontWeight: 600, letterSpacing: '0.06em' }}>
                  {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                </span>
                <div className="flex gap-1">
                  {[{ dir: 'prev', Icon: ChevronLeft }, { dir: 'next', Icon: ChevronRight }].map(({ dir, Icon }) => (
                    <button
                      key={dir}
                      onClick={() => navigate(dir)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 hover:bg-white/15"
                      style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)' }}
                    >
                      <Icon className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.60)' }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile CTA — phones only, below dots */}
              <div
                className="flex md:hidden mb-6"
                style={{ opacity: textIn ? 1 : 0, transition: ease(540) }}
              >
                <Link
                  to={slide.ctaLink}
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm hover:scale-105 hover:-translate-y-0.5 transition-all duration-200"
                  style={{
                    background: 'linear-gradient(135deg, #cd9623, #e8b84b)',
                    color: '#1a0030',
                    boxShadow: '0 6px 20px rgba(205,150,35,0.38)',
                  }}
                >
                  {slide.cta}
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>

              {/* Metrics — tablet and up */}
              <div
                className="hidden md:flex flex-wrap gap-4 lg:gap-7"
                style={{ opacity: textIn ? 1 : 0, transition: ease(560) }}
              >
                {metrics.map((m, i) => {
                  const MIcon = m.icon;
                  return (
                    <div key={i} className="flex items-center gap-2.5 group cursor-default">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200"
                        style={{ background: 'rgba(205,150,35,0.12)', border: '1px solid rgba(205,150,35,0.28)' }}
                      >
                        <MIcon className="w-4 h-4" style={{ color: '#e8b84b' }} />
                      </div>
                      <div>
                        <p className="font-bold text-sm leading-none mb-0.5" style={{ color: 'rgba(255,255,255,0.90)' }}>{m.value}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.40)' }}>{m.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ══ RIGHT — Card B + slide pills ═════════════════ */}
            <div className="hidden md:flex flex-col items-end gap-4 lg:gap-6 lg:-mt-12 lg:-mr-8">

              {/* Card B */}
              <div className="w-full" style={{ maxWidth: 520, opacity: 1, transition: 'opacity 0.55s ease 160ms' }}>
                <div style={{ ...card3D, borderRadius: 20, overflow: 'hidden' }}>

                  {/* Logos grid — subsidiaries slide */}
                  {slide.logos ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, background: '#fff' }}>
                      {slide.logos.map((logo, li) => (
                        <div key={li} style={{
                          flex: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: '20px 28px',
                          borderBottom: li < slide.logos.length - 1 ? '1px solid #f1f5f9' : 'none',
                        }}>
                          <img src={logo.src} alt={logo.name} style={{ maxHeight: li === 1 ? 100 : 72, maxWidth: '80%', objectFit: 'contain' }} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Crossfade image — all other slides */
                    <div
                      className="relative h-[260px] md:h-[300px] lg:h-[380px] overflow-hidden"
                      style={{ borderTopLeftRadius: 19, borderTopRightRadius: 19 }}>
                      {slides.map((s, i) => (
                        <img key={i} src={s.mainImage} alt={s.tag}
                          style={{
                            position: 'absolute', inset: 0,
                            width: '100%', height: '100%', objectFit: 'cover',
                            objectPosition: i === 1 ? '50% 65%' : 'center',
                            opacity: current === i ? 1 : 0,
                            transform: current === i ? 'scale(1.06)' : 'scale(1)',
                            transition: 'opacity 1s ease, transform 9s ease',
                          }}
                        />
                      ))}
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.40) 0%, transparent 60%)' }} />
                      <span style={{
                        position: 'absolute', top: 14, left: 14,
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: 'rgba(255,255,255,0.90)', backdropFilter: 'blur(8px)',
                        color: slide.accent, padding: '5px 14px', borderRadius: 20,
                        fontSize: 11, fontWeight: 700, transition: 'color 0.8s',
                      }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: slide.accent, display: 'inline-block', transition: 'background 0.8s' }} />
                        {slide.tag}
                      </span>
                    </div>
                  )}

                  {/* Body — hidden on subsidiaries slide */}
                  {!slide.logos && (
                    <div style={{ padding: '20px 26px 24px' }}>
                      <p style={{ fontSize: 22, fontWeight: 800, color: '#1e1b4b', margin: '0 0 3px', lineHeight: 1.2 }}>
                        {slide.statValue}
                      </p>
                      <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 14px' }}>{slide.statLabel}</p>
                      <div style={{ height: 3, background: '#f1f5f9', borderRadius: 3 }}>
                        <div style={{
                          height: '100%', width: `${progress}%`,
                          background: `linear-gradient(90deg, ${slide.accent}, rgba(${slide.accentRgb},0.5))`,
                          borderRadius: 3, transition: 'width 0.05s linear, background 1s',
                        }} />
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Slide selector pills */}
              <div className="flex flex-wrap gap-2 lg:gap-2.5 pr-1 w-full justify-end">
                {slides.map((s, i) => {
                  const SIcon = s.icon;
                  const isActive = i === current;
                  return (
                    <button
                      key={i}
                      onClick={() => goToIndex(i)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-300 backdrop-blur-md"
                      style={{
                        background: isActive ? `${s.accent}18` : 'rgba(255,255,255,0.04)',
                        borderColor: isActive ? `${s.accent}65` : 'rgba(255,255,255,0.08)',
                        transform: isActive ? 'translateY(-2px)' : 'none',
                        boxShadow: isActive ? `0 8px 24px ${s.accent}28` : 'none',
                      }}
                    >
                      <SIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isActive ? s.accent : '#6b7280' }} />
                      <span className="text-xs font-bold tracking-wide" style={{ color: isActive ? s.accent : '#6b7280' }}>
                        {s.tag}
                      </span>
                    </button>
                  );
                })}
              </div>

            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
